import React, { useState } from "react";
import {
  IconButton,
  Label,
  Stack,
  Text,
  MessageBar,
  MessageBarType,
} from "@fluentui/react";
import TextInput from "@/ui-controls/TextInput";
import { Grid } from "fluentui-react-grid";

export interface PropertyEntry {
  id?: number;
  propertyName: string;
  propertyValue: string;
}

interface Props {
  properties: PropertyEntry[];
  onChange: (properties: PropertyEntry[]) => void;
  disabled?: boolean;
}

const ConfigurationProperties = (props: Props) => {
  const { properties, onChange, disabled } = props;
  const [error, setError] = useState("");

  const handleAdd = () => {
    setError("");
    onChange([...properties, { propertyName: "", propertyValue: "" }]);
  };

  const handleRemove = (index: number) => {
    setError("");
    const updated = properties.filter((_, i) => i !== index);
    onChange(updated);
  };

  const handleChange = (
    index: number,
    field: "propertyName" | "propertyValue",
    value: string
  ) => {
    setError("");
    const updated = properties.map((p, i) =>
      i === index ? { ...p, [field]: value } : p
    );

    if (field === "propertyName") {
      const names = updated.map((p) => p.propertyName.trim().toLowerCase());
      const name = value.trim().toLowerCase();
      if (name && names.filter((n) => n === name).length > 1) {
        setError(`Duplicate property name: "${value.trim()}"`);
      }
    }

    onChange(updated);
  };

  return (
    <div>
      <Stack
        horizontal
        verticalAlign="center"
        tokens={{ childrenGap: 8 }}
        styles={{ root: { marginBottom: 4 } }}
      >
        <Label>Extended Properties</Label>
        <IconButton
          iconProps={{ iconName: "Add" }}
          title="Add property"
          ariaLabel="Add property"
          onClick={handleAdd}
          disabled={disabled}
          styles={{
            root: { width: 28, height: 28 },
            icon: { fontSize: 14, color: "#0078d4" },
          }}
        />
      </Stack>
      {error && (
        <MessageBar
          messageBarType={MessageBarType.warning}
          onDismiss={() => setError("")}
          styles={{ root: { marginBottom: 8 } }}
        >
          {error}
        </MessageBar>
      )}
      {properties.length === 0 && (
        <Text
          variant="small"
          styles={{ root: { color: "#605e5c", fontStyle: "italic" } }}
        >
          No extended properties configured.
        </Text>
      )}
      {properties.map((prop, index) => (
        <Grid dir="ltr" key={index}>
          <Grid.Row>
            <Grid.Col sizeSm={5}>
              <TextInput
                label={index === 0 ? "Name" : ""}
                baseTypeProps={{
                  placeholder: "Property name",
                  value: prop.propertyName,
                  onChange: (_: any, val: string) =>
                    handleChange(index, "propertyName", val || ""),
                  disabled,
                  maxLength: 100,
                }}
              />
            </Grid.Col>
            <Grid.Col sizeSm={6}>
              <TextInput
                label={index === 0 ? "Value" : ""}
                baseTypeProps={{
                  placeholder: "Property value",
                  value: prop.propertyValue,
                  onChange: (_: any, val: string) =>
                    handleChange(index, "propertyValue", val || ""),
                  disabled,
                  type: "password",
                  canRevealPassword: true,
                }}
              />
            </Grid.Col>
            <Grid.Col sizeSm={1}>
              <IconButton
                iconProps={{ iconName: "Delete" }}
                title="Remove property"
                ariaLabel="Remove property"
                onClick={() => handleRemove(index)}
                disabled={disabled}
                styles={{
                  root: { marginTop: index === 0 ? 29 : 0 },
                  icon: { color: "#a80000" },
                }}
              />
            </Grid.Col>
          </Grid.Row>
        </Grid>
      ))}
    </div>
  );
};

export default ConfigurationProperties;
